import React, { useRef } from "react";
import dayjs from "dayjs";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { Button } from "@components/button";
import { TimePicker } from "@components/timePicker";
import { RangePicker } from "@components/rangePicker";
import { IDateRangeFilter, IFilterOption } from "../filterModal.types";
import {
    datePickerRow,
    datePickerTitle,
    datePickerTitleWrapper,
    desktopPickerMainContainer,
    desktopTimePickerContainer,
    rangePickerDropdown,
    rangePickerPopupContainer,
    StyledDateRangeChip,
    StyledDateRangeChipGroup,
    timePicker,
    timePickerPopup,
} from "../filterModal.styles";
import {
    END_INPUT,
    FILTER_MODAL_DATA_IDS,
    FILTERS_DISPLAY_DATE_FORMAT,
    FILTERS_TIME_FORMAT,
    START_INPUT,
    TIMEPICKER_COMMON_PROPS,
} from "../filterModal.constants";
import useDateRangeFilter from "./useDateRangeFilter";

export interface IDateFilter {
    from: number;
    to: number;
    rangeType: string;
}

const DateRangeFilter = <T extends Record<keyof T, IFilterOption[]>>(
    props: IDateRangeFilter<T>
) => {
    const endTimeRef = useRef<HTMLDivElement | null>(null);
    const startTimeRef = useRef<HTMLDivElement | null>(null);
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const tokens = useEnigmaToken();
    const {
        filterOptions,
        localSelectedFilter,
        showTimePicker,
        isCustomDisabled,
        minDate,
        maxDate,
        handleChipClick,
        handleStartTimeChange,
        handleEndTimeChange,
        handleCalendarChange,
    } = useDateRangeFilter(props);

    const _renderTimePicker = () => (
        <>
            <div className={timePicker(tokens)} ref={startTimeRef}>
                <div>Start Time: </div>
                <TimePicker
                    {...TIMEPICKER_COMMON_PROPS}
                    getPopupContainer={() =>
                        startTimeRef.current as HTMLElement
                    }
                    onChange={handleStartTimeChange}
                    value={dayjs(
                        dayjs(localSelectedFilter.from),
                        FILTERS_TIME_FORMAT
                    )}
                    popupClassName={timePickerPopup}
                    disabled={isCustomDisabled}
                    data-testid={FILTER_MODAL_DATA_IDS.DATE_PICKER_START_TIME}
                />
            </div>
            <div className={timePicker(tokens)} ref={endTimeRef}>
                <div>End Time: </div>
                <TimePicker
                    {...TIMEPICKER_COMMON_PROPS}
                    getPopupContainer={() => endTimeRef.current as HTMLElement}
                    onChange={handleEndTimeChange}
                    value={dayjs(
                        dayjs(localSelectedFilter.to),
                        FILTERS_TIME_FORMAT
                    )}
                    popupClassName={timePickerPopup}
                    disabled={isCustomDisabled}
                    data-testid={FILTER_MODAL_DATA_IDS.DATE_PICKER_END_TIME}
                />
            </div>
        </>
    );

    return (
        <StyledDateRangeChip tokens={tokens}>
            <StyledDateRangeChipGroup
                tokens={tokens}
                data-testid={FILTER_MODAL_DATA_IDS.CHECKBOX_GROUP}
            >
                {!(
                    filterOptions.length === 1 &&
                    filterOptions[0].key === "custom"
                ) &&
                    filterOptions.map((filterItem) => {
                        const _isSelected =
                            localSelectedFilter.rangeType === filterItem.value;

                        return (
                            <Button
                                key={filterItem.key}
                                type={_isSelected ? "tonal" : "secondary"}
                                onClick={() => handleChipClick(filterItem)}
                            >
                                {filterItem.title}
                            </Button>
                        );
                    })}
            </StyledDateRangeChipGroup>
            <div className={datePickerRow(tokens)}>
                <div className={datePickerTitleWrapper(tokens)}>
                    <div className={datePickerTitle(tokens)}>From</div>
                    <div className={datePickerTitle(tokens)}>To</div>
                </div>
                <div ref={calendarRef} className={desktopPickerMainContainer}>
                    <RangePicker
                        inputReadOnly
                        readOnly={isCustomDisabled}
                        disabledDate={() => isCustomDisabled}
                        open
                        allowClear={false}
                        format={[
                            FILTERS_DISPLAY_DATE_FORMAT,
                            FILTERS_DISPLAY_DATE_FORMAT,
                        ]}
                        id={{
                            start: START_INPUT,
                            end: END_INPUT,
                        }}
                        placeholder={["From", "To"]}
                        minDate={dayjs(minDate)}
                        maxDate={dayjs(maxDate)}
                        value={[
                            dayjs(localSelectedFilter.from),
                            dayjs(localSelectedFilter.to),
                        ]}
                        getPopupContainer={() =>
                            calendarRef.current as HTMLElement
                        }
                        suffixIcon={null}
                        separator={null}
                        className={rangePickerDropdown(tokens)}
                        popupClassName={rangePickerPopupContainer(
                            isCustomDisabled,
                            tokens
                        )}
                        onCalendarChange={handleCalendarChange}
                    />
                </div>

                {showTimePicker && (
                    <div className={desktopTimePickerContainer}>
                        {_renderTimePicker()}
                    </div>
                )}
            </div>
        </StyledDateRangeChip>
    );
};

export default DateRangeFilter;
